import { DialogCustom } from 'components/DialogCustom';
import { DownloadAppModal } from 'components/Forms/TutorForm/DownloadAppModal';
import { Snackbar } from 'components/Snackbar';
import { useDisclose, useLoader } from 'hooks/utilHooks';
import {
  createContext,
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import {
  ICouponValues,
  TutorUserPlanService,
} from 'services/tutor-user-plan.service';
import { TutorUserService } from 'services/tutor-user.service';
import {
  ITutorUserCreate,
  ITutorUserCreateOrContractPlanResponse,
} from 'types/TutorUser';

import { AlertColor } from '@mui/material';
import { IPlan } from 'types/Plan';
import { maskCreditCardExpires } from 'util/masks';
import { useTutorUserPlan } from 'context';
import axios from 'axios';

export interface ITutorUserRegistrationContext {
  loading: boolean;
  form: UseFormReturn<ITutorUserCreate, any>;
  plans: IPlan[];
  resume: ITutorUserCreateOrContractPlanResponse | null;
  chosenPlan?: string;
  setChosenPlan: Dispatch<SetStateAction<string | undefined>>;
  getEmailValidationToken: () => Promise<void>;
  handleBasicInformationsStep: () => void;
  handlePrevious: () => void;
  finishWithoutPlan: () => void;
  continueToPayment: () => void;
  continueToAddress: () => void;
  finishWithPlan: () => void;
  pjBankCreditCardInputRef: MutableRefObject<HTMLInputElement | null>;
  applyCoupon: (ticketName: string, planId: string) => void;
  couponValues: ICouponValues | undefined;
  setCouponValues: Function;
}

interface ISnackbar {
  message: string | null;
  variant?: AlertColor | undefined;
  duration?: number;
}

export const TutorUserRegistrationContext =
  createContext<ITutorUserRegistrationContext>(
    {} as ITutorUserRegistrationContext
  );

type Props = {
  children: ReactNode;
};

export const TutorUserRegistrationProvider = ({ children }: Props) => {
  const [snackbar, setSnackbar] = useState<ISnackbar>({
    message: '',
    variant: 'info',
  });
  const { hideLoader, loading, showLoader } = useLoader();
  const finishModal = useDisclose();
  const downloadAppModal = useDisclose();
  const [plans, setPlans] = useState<IPlan[]>([]);
  const [chosenPlan, setChosenPlan] = useState<string>();
  const [couponValues, setCouponValues] = useState<ICouponValues>();
  const [canGoNext, setCanGoNext] = useState<Boolean>(false);
  const loadingCoupon = useLoader();
  const [resume, setResume] =
    useState<ITutorUserCreateOrContractPlanResponse | null>(null);
  const pjBankCreditCardInputRef =
    useRef<HTMLInputElement>() as MutableRefObject<HTMLInputElement>;

  const form = useForm<ITutorUserCreate>({
    mode: 'onBlur',
    defaultValues: {
      activeStep: 0,
      name: '',
      cpf: '',
      email: '',
      phone: '',
      password: '',
      passwordConfirmation: '',
      address: {
        cep: '',
        street: '',
        number: '',
        neighborhood: '',
        city: '',
        state: '',
        complement: '',
      },
      billingAddress: {
        cep: '',
        street: '',
        number: '',
        neighborhood: '',
        city: '',
        state: '',
        complement: '',
      },
      creditCard: {
        number: '',
        name: '',
        expires: '',
        cvv: '',
        cardHash: '',
        cpf: '',
      },
      ticket: '',
      acceptTerms: false,
      token: '',
      plan: '',
    },
  });

  const { setValue } = form;

  const getEmailValidationToken = async () => {
    const { name, email } = form.getValues();

    if (loading) {
      return;
    }

    try {
      showLoader();
      await TutorUserService.requestEmailValidationCode({ name, email });

      form.setValue('activeStep', 1);
    } catch (reason) {
      const { message } = reason as Error;

      setSnackbar({
        message,
        variant: 'error',
        duration: 6000,
      });
    } finally {
      hideLoader();
    }
  };

  const handleBasicInformationsStep = async () => {
    await getEmailValidationToken();
  };

  const handlePrevious = () => {
    const { activeStep } = form.getValues();

    if (activeStep <= 0) {
      return;
    }

    form.setValue('activeStep', activeStep - 1);
  };

  const handleFinishWithoutPlanConfirmation = async () => {
    try {
      showLoader();
      const values = form.getValues();

      await TutorUserService.createTutorUser({
        ...values,
        creditCard: undefined,
      });

      setSnackbar({
        message: 'Cadastro realizado com sucesso!',
        variant: 'success',
        duration: 6000,
      });

      downloadAppModal.onOpen();

      form.reset({}, { keepDirty: false, keepValues: true });
    } catch (reason) {
      const { message } = reason as Error;

      setSnackbar({
        message,
        variant: 'error',
        duration: 6000,
      });
    } finally {
      hideLoader();
      finishModal.onClose();
    }
  };

  const handleFinishWithPlan = async () => {
    try {
      const values = form.getValues();
      const { creditCard, ticket, billingAddress  } = values;
      if (!creditCard) {
        setSnackbar({
          message:
            'Número do cartão inválido. Atualize o número do cartão e tente novamente',
          variant: 'error',
          duration: 6000,
        });

        return;
      }

      showLoader();
      const year = creditCard.expires.substring(2,7);
      const month = creditCard.expires.substring(0,2);

      const options: any = {
        method: 'POST',
        url: 'https://api.pagar.me/core/v5/tokens',
        params: { appId: 'pk_82mlKx9fGFmwajkn' },
        //params: { appId: 'pk_test_jG9OwWbUDmcBO7lq' },
        headers: { accept: 'application/json', 'content-type': 'application/json' },
        data: {
          type: 'card',
          card: {
            number: creditCard?.number?.replace(/\s/g, ''),
            holder_name: creditCard?.name,
            holder_document: creditCard?.cpf,
            exp_month: Number(month),
            exp_year: Number(year),
            cvv: creditCard?.cvv
          }
        }
      };

      await axios
      .request(options)
      .then(async function (response) {
        const resp = await TutorUserService.createTutorUser({
          ...values,
          cardHash: response.data.id,
          planId: chosenPlan,
          ticket: ticket ?? undefined,
          holderName: creditCard.name,
          billingAddress: {
            line_1: `${billingAddress.number}, ${billingAddress.street}, ${billingAddress.neighborhood}`,
            line_2: billingAddress.complement ?? '',
            zip_code: billingAddress.cep,
            city: billingAddress.city,
            state: billingAddress.state,
            country: 'BR',
          }
        });
 
        const { tutorPlan, plan: planResponse } = resp;

        if (!planResponse) {
          setCanGoNext(false);
          setSnackbar({
            message: resp.message,
            variant: 'error',
            duration: 6000,
          });
        } else {
          setResume({
            message: 'Assinatura criada com sucesso',
            plan: planResponse,
            tutorPlan,
            creditCard: {
              description: `Na fatura do seu cartão você verá o pagamento em nome do  PG # VET4ALLSAÚDE LTDA`,
              flag: resp?.tutorPlan?.card.brand,
              maskedNumber: resp?.tutorPlan?.card.first_six_digits + '******' + resp.tutorPlan.card.last_four_digits,
              title: ``
            }
          });
          //setCanGoNext(true);
          form.setValue('activeStep', 4);
        }
        
       

      })
      .catch(function (error) {
        setSnackbar({
          message: error.message,
          variant: 'error',
          duration: 6000,
        });
      });
      

      
      if (canGoNext) {
        setSnackbar({
          message: 'Cadastro realizado com sucesso!',
          variant: 'success',
          duration: 6000,
        });
        
        form.setValue('activeStep', 4);
  
        // form.reset(
        //   {
        //     activeStep: 4,
        //   },
        //   { keepDirty: false }
        // );
      }
    } catch (reason) {
      const { message } = reason as Error;

      setSnackbar({
        message,
        variant: 'error',
        duration: 6000,
      });
    } finally {
      hideLoader();
    }
  };

  const handleContinueToPayment = async () => {
    form.setValue('activeStep', 2);
  };

  const handleContinueToAddress = async () => {
    form.setValue('activeStep', 3);

    setTimeout(() => {
      /* Force form to reset submited status without lose values */
      form.reset(
        {},
        {
          keepValues: true,
          keepTouched: true,
          keepIsValid: true,
          keepDefaultValues: true,
        }
      );
    }, 500);
  };

  // FIXME: This function already have in other context (TutorUserPlan)
  const applyCoupon = async (ticketName: string, planId: string) => {
    try {
      loadingCoupon.showLoader();

      await TutorUserPlanService.getCouponByName({ ticketName, planId }).then(
        setCouponValues
      );

      setSnackbar({
        message: `Cupom ${ticketName} aplicado!`,
        variant: 'success',
        duration: 6000,
      });
    } catch (reason) {
      const { message } = reason as Error;

      setSnackbar({
        message,
        variant: 'error',
        duration: 6000,
      });
    } finally {
      loadingCoupon.hideLoader();
    }
  };

  useEffect(() => {
    TutorUserPlanService.getAllPlans()
      .then((plansList) => {
        setPlans(plansList);
        if (plansList[0]) {
          setValue('plan', plansList[0].id);
        }
      })
      .catch((reason) => {
        const { message } = reason as Error;

        setSnackbar({
          message,
          variant: 'error',
          duration: 6000,
        });
      });
  }, [setValue]);

  return (
    <form>
      <FormProvider {...form}>
        <TutorUserRegistrationContext.Provider
          value={{
            loading,
            form,
            plans,
            resume,
            getEmailValidationToken,
            handleBasicInformationsStep,
            chosenPlan,
            setChosenPlan,
            handlePrevious,
            continueToAddress: form.handleSubmit(handleContinueToAddress),
            finishWithoutPlan: form.handleSubmit(finishModal.onOpen),
            continueToPayment: form.handleSubmit(handleContinueToPayment),
            finishWithPlan: form.handleSubmit(handleFinishWithPlan),
            pjBankCreditCardInputRef,
            applyCoupon,
            couponValues,
            setCouponValues,
          }}
        >
          {children}

          {snackbar.message ? (
            <Snackbar
              text={snackbar.message}
              snackbarOpen={true}
              setSnackbarOpen={() => {
                setSnackbar({
                  message: '',
                });
              }}
              autoHideDuration={snackbar.duration}
              severityType={snackbar.variant}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            />
          ) : null}

          <DialogCustom
            title="Finalizar cadastro sem assinar plano"
            text="Ao realizar a assinatura você terá acesso aos atendimentos veterinários por vídeo chamada. Você deseja continuar sem assinar plano?"
            isOpen={finishModal.isOpen}
            onClose={finishModal.onClose}
            onClick={handleFinishWithoutPlanConfirmation}
            loading={loading}
          />

          <DownloadAppModal
            open={downloadAppModal.isOpen}
            onClose={downloadAppModal.onClose}
          />
        </TutorUserRegistrationContext.Provider>
      </FormProvider>
    </form>
  );
};

export const useTutorUserRegistration = () => {
  return useContext(TutorUserRegistrationContext);
};
